import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    Button,
    SelectChangeEvent,
    Link
} from '@mui/material';
import theme from '../theme';
import { settingsService } from '../services/SettingsService';
import Toast from '../components/utils/Toast';
import { fullScreenButtonStyle } from '../utils/buttonStyles';
import { track } from '@/third-party/MixPanel';

const Feedback: React.FC = () => {
    const { t } = useTranslation();
    const [category, setCategory] = useState('Experiences');
    const [topic, setTopic] = useState('App functionality');
    const [details, setDetails] = useState('');
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

    const handleSubmit = async () => {
        try {
            await settingsService.submitFeedback({
                category,
                topic,
                details
            });
            setToastMessage(t('feedback.thankYou'));
            setToastSeverity('success');
            setToastOpen(true);
            setDetails('');
            track('feedback_submitted', {
                category,
                topic,
                details
            });
        } catch (error) {
            setToastMessage(t('feedback.errorSubmitting'));
            setToastSeverity('error');
            setToastOpen(true);
            console.error('Error submitting feedback:', error);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ p: 3, pt: 0, maxWidth: 600, mx: 'auto' }}>

                <Typography sx={{ mb: 3, mt: 4 }}>
                    {t('feedback.title')}
                    {t('feedback.description')}
                </Typography>

                <Typography sx={{ mb: 1, display: 'none' }}>
                    {t('feedback.helpCenter')}
                    <Link href="/help" underline="always">{t('feedback.helpCenterLink')}</Link> or{' '}
                    <Link href="/contact" underline="always">{t('feedback.contactLink')}</Link> to connect with
                    our support team.
                </Typography>

                <Box sx={{ mt: 4 }}>
                    <Typography sx={{ mb: 1 }}>{t('feedback.feedbackAbout')}</Typography>
                    <Select
                        fullWidth
                        value={category}
                        defaultValue='Experiences'
                        onChange={(e: SelectChangeEvent) => setCategory(e.target.value)}
                        sx={{ mb: 3 }}
                    >
                        <MenuItem value="Experiences">{t('feedback.categories.experiences')}</MenuItem>
                        <MenuItem value="Technical">{t('feedback.categories.technical')}</MenuItem>
                        <MenuItem value="Other">{t('feedback.categories.other')}</MenuItem>
                    </Select>

                    <Typography sx={{ mb: 1 }}>{t('feedback.topicFeature')}</Typography>
                    <Select
                        fullWidth
                        value={topic}
                        defaultValue='App functionality'
                        onChange={(e: SelectChangeEvent) => setTopic(e.target.value)}
                        sx={{ mb: 3 }}
                    >
                        <MenuItem value="Experience quality & communication">
                            {t('feedback.topics.experienceQuality')}
                        </MenuItem>
                        <MenuItem value="App functionality">{t('feedback.topics.appFunctionality')}</MenuItem>
                        <MenuItem value="Reading experience">{t('feedback.topics.readingExperience')}</MenuItem>
                        <MenuItem value="Journal features">{t('feedback.topics.journalFeatures')}</MenuItem>
                        <MenuItem value="Account & settings">{t('feedback.topics.accountSettings')}</MenuItem>
                        <MenuItem value="Performance & stability">{t('feedback.topics.performanceStability')}</MenuItem>
                        <MenuItem value="Other">{t('feedback.topics.other')}</MenuItem>
                    </Select>

                    <Typography sx={{ mb: 1 }}>{t('feedback.addDetails')}</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        sx={{ mb: 4 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        fullWidth
                        sx={fullScreenButtonStyle}
                    >
                        {t('feedback.submit')}
                    </Button>
                </Box>

                <Toast
                    open={toastOpen}
                    message={toastMessage}
                    severity={toastSeverity}
                    onClose={() => setToastOpen(false)}
                />
            </Box>
        </ThemeProvider>
    );
};

export default Feedback;
